import { useCallback } from 'react';
import liff from '@line/liff';
import { useStore } from '../store';
import { api } from '../api';

const LIFF_ID = import.meta.env.VITE_LIFF_ID || '2009047147-tb7X4P6v';

// Check if running in LIFF/LINE environment
const isInLiffBrowser = () => {
    return window.location.hostname.includes('liff.line.me') ||
        navigator.userAgent.includes('Line');
};

export function useLiff() {
    const setReady = useStore((s) => s.setReady);
    const setUser = useStore((s) => s.setUser);
    const setDbUser = useStore((s) => s.setDbUser);
    const setTransactions = useStore((s) => s.setTransactions);
    const setPromptPay = useStore((s) => s.setPromptPay);

    const initLiff = useCallback(async () => {
        const startTime = Date.now();
        const MIN_LOADING_TIME = 1500;

        try {
            // Try to init LIFF
            await liff.init({ liffId: LIFF_ID });
            console.log('LIFF initialized successfully');

            if (liff.isLoggedIn()) {
                const profile = await liff.getProfile();
                console.log('Got LIFF profile:', profile.displayName);
                setUser(profile);
                await syncUser(profile);
            } else {
                // Force login if in LIFF browser
                console.log('Not logged in, redirecting to login...');
                liff.login();
                return; // Don't setReady, wait for redirect back
            }
        } catch (error: any) {
            console.error('LIFF init error:', error);
            // If token revoked/expired, force re-login
            if (error?.message?.includes('revoked') || error?.message?.includes('expired')) {
                try { liff.logout(); } catch {}
                liff.login();
                return;
            }
        }

        const elapsed = Date.now() - startTime;
        const remaining = MIN_LOADING_TIME - elapsed;
        if (remaining > 0) {
            await new Promise(resolve => setTimeout(resolve, remaining));
        }

        setReady(true);
    }, [setReady, setUser]);

    const syncUser = async (profile: { userId: string; displayName: string; pictureUrl?: string }) => {
        try {
            // Get or create user
            let result = await api.getUser(profile.userId);

            if (!result.data) {
                result = await api.createUser({
                    line_user_id: profile.userId,
                    display_name: profile.displayName,
                    picture_url: profile.pictureUrl,
                });
            }

            if (result.data) {
                setDbUser(result.data);

                // If user is STAFF, load Owner's data instead
                if (result.data.role === 'STAFF' && result.data.parent_owner_id) {
                    try {
                        // Get store info which includes owner's PromptPay
                        const storeInfo = await api.getStoreInfo(profile.userId);
                        if (storeInfo.store) {
                            // Use Owner's PromptPay settings
                            setPromptPay({
                                number: storeInfo.store.promptpay_number || '',
                                name: storeInfo.store.promptpay_name || '',
                                type: 'phone',
                            });
                            // Load transactions using Owner's ID (not Staff's)
                            const txResult = await api.getTransactions(storeInfo.store.owner_line_user_id, 50);
                            if (txResult.data) {
                                setTransactions(txResult.data);
                            }
                        }
                    } catch (err) {
                        console.error('Error loading store info:', err);
                    }
                } else {
                    // OWNER: Load own PromptPay settings
                    const ppResult = await api.getPromptPaySettings(profile.userId);
                    if (ppResult.data) {
                        setPromptPay(ppResult.data);
                    }

                    // Load own transactions
                    const txResult = await api.getTransactions(profile.userId, 50);
                    if (txResult.data) {
                        setTransactions(txResult.data);
                    }
                }
            }
        } catch (error) {
            console.error('Sync error:', error);
        }
    };

    const login = useCallback(() => {
        if (!liff.isLoggedIn()) {
            liff.login();
        }
    }, []);

    const logout = useCallback(() => {
        if (liff.isLoggedIn()) {
            liff.logout();
            window.location.reload();
        }
    }, []);

    return { initLiff, login, logout };
}
